import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, MapPin, Clock, Phone, ExternalLink, CheckCircle2 } from 'lucide-react';
import type { Resource, ResourceCategory } from '@/contexts/ResourceContext';

const categoryColors: Record<ResourceCategory, string> = {
  food: '#22c55e',
  shelter: '#8b5cf6',
  medical: '#ef4444',
  skills: '#3b82f6',
  tools: '#f59e0b',
  transport: '#06b6d4',
  legal: '#ec4899',
  childcare: '#f97316',
  elderlycare: '#6366f1',
  'mental-health': '#a855f7',
  community: '#14b8a6',
  other: '#6b7280',
};

const categoryLabels: Record<ResourceCategory, string> = {
  food: 'Food & Groceries',
  shelter: 'Shelter & Housing',
  medical: 'Health Care',
  skills: 'Skills & Learning',
  tools: 'Tools & Equipment',
  transport: 'Transportation',
  legal: 'Legal Aid',
  childcare: 'Childcare',
  elderlycare: 'Elder Care',
  'mental-health': 'Mental Health',
  community: 'Community',
  other: 'Other',
};

interface GraphNode {
  resource: Resource;
  x: number;
  y: number;
  color: string;
}

interface ConnectionType {
  from: GraphNode;
  to: GraphNode;
  opacity: number;
  strength: number;
  types: string[];
}

interface ResourceGraphProps {
  resources: Resource[];
  onResourceClick: (resource: Resource) => void;
  selectedId?: string;
}

// Complementary categories that flow well together
const complementaryCategories: Record<ResourceCategory, ResourceCategory[]> = {
  food: ['community', 'tools'],
  shelter: ['community', 'legal'],
  medical: ['community', 'mental-health'],
  skills: ['community', 'tools', 'food'],
  tools: ['community', 'skills', 'transport'],
  transport: ['community', 'shelter'],
  legal: ['community', 'shelter'],
  childcare: ['community', 'skills'],
  elderlycare: ['community', 'medical'],
  'mental-health': ['community', 'medical'],
  community: ['food', 'skills', 'tools', 'medical', 'mental-health', 'shelter'],
  other: [],
};

export function ResourceGraph({ resources, onResourceClick, selectedId }: ResourceGraphProps) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredConnection, setHoveredConnection] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [animationProgress, setAnimationProgress] = useState(0);

  // Animate on mount
  useEffect(() => {
    const startTime = Date.now();
    const duration = 1000;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setAnimationProgress(easeOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, []);

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      const container = document.getElementById('graph-container');
      if (container) {
        setDimensions({
          width: container.clientWidth,
          height: Math.max(350, Math.min(600, window.innerHeight * 0.4)),
        });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Calculate node positions
  const nodes = useMemo<GraphNode[]>(() => {
    if (resources.length === 0) return [];

    const padding = 80;
    const effectiveWidth = dimensions.width - padding * 2;
    const effectiveHeight = dimensions.height - padding * 2;

    // Calculate bounds
    const lats = resources.map(r => r.latitude);
    const lngs = resources.map(r => r.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const latRange = maxLat - minLat || 0.01;
    const lngRange = maxLng - minLng || 0.01;

    return resources.map((resource, index) => {
      // Calculate position based on coordinates
      let x: number, y: number;
      
      if (resources.length === 1) {
        // Center single node
        x = dimensions.width / 2;
        y = dimensions.height / 2;
      } else {
        // Map coordinates to canvas
        x = padding + ((resource.longitude - minLng) / lngRange) * effectiveWidth;
        y = padding + ((maxLat - resource.latitude) / latRange) * effectiveHeight;
        
        // Add some randomness to avoid overlap
        const angle = (index / resources.length) * Math.PI * 2;
        const radius = Math.min(effectiveWidth, effectiveHeight) * 0.1;
        x += Math.cos(angle) * radius * 0.5;
        y += Math.sin(angle) * radius * 0.5;
      }

      return {
        resource,
        x,
        y,
        color: categoryColors[resource.category],
      };
    });
  }, [resources, dimensions]);

  // Calculate weighted connections between nodes
  const connections = useMemo<ConnectionType[]>(() => {
    const conns: ConnectionType[] = [];
    
    nodes.forEach((node, i) => {
      nodes.forEach((other, j) => {
        if (i < j) {
          const types: string[] = [];
          let strength = 0;
          
          // 1. Shared tags connection (strongest)
          const sharedTags = node.resource.tags.filter(tag => 
            other.resource.tags.includes(tag)
          );
          if (sharedTags.length > 0) {
            strength += sharedTags.length * 3; // 3 points per shared tag
            types.push(`Shared: ${sharedTags.join(', ')}`);
          }
          
          // 2. Geographic proximity
          const pixelDistance = Math.sqrt(
            Math.pow(node.x - other.x, 2) + Math.pow(node.y - other.y, 2)
          );
          const maxPixelDistance = Math.min(dimensions.width, dimensions.height) * 0.35;
          if (pixelDistance < maxPixelDistance) {
            const proximityBonus = Math.round((1 - pixelDistance / maxPixelDistance) * 2);
            strength += proximityBonus;
            types.push(`Nearby`);
          }
          
          // 3. Category complementarity
          const isComplementary = 
            complementaryCategories[node.resource.category]?.includes(other.resource.category) ||
            complementaryCategories[other.resource.category]?.includes(node.resource.category);
          if (isComplementary) {
            strength += 1;
            types.push(`Complementary`);
          }
          
          // Only create connection if there's some value flow potential
          if (strength > 0) {
            const opacity = Math.min(0.4, strength * 0.08 + 0.1);
            conns.push({ from: node, to: other, opacity, strength, types });
          }
        }
      });
    });
    
    // Sort by strength for layering
    return conns.sort((a, b) => a.strength - b.strength);
  }, [nodes, dimensions]);

  if (resources.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-16 px-8">
          <div className="text-center max-w-sm mx-auto space-y-4">
            <div className="w-16 h-16 rounded-full bg-muted mx-auto flex items-center justify-center">
              <MapPin className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">No resources to visualize</h3>
            <p className="text-muted-foreground">
              Add some resources to see them appear in the graph
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="relative">
      {/* SVG Graph with enhanced background */}
      <div 
        id="graph-container" 
        className="w-full overflow-hidden rounded-2xl"
        style={{
          background: `
            linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)) 100%),
            repeating-linear-gradient(0deg, transparent, transparent 40px, hsl(var(--border)) 40px, hsl(var(--border)) 41px),
            repeating-linear-gradient(90deg, transparent, transparent 40px, hsl(var(--border)) 40px, hsl(var(--border)) 41px)
          `,
          backgroundBlendMode: 'overlay, normal, normal'
        }}
      >
        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
        
        <svg
          width={dimensions.width}
          height={dimensions.height}
          className="w-full h-auto relative z-10"
          style={{ minHeight: '350px' }}
        >
          <defs>
            {/* Glow filters for nodes */}
            {Object.entries(categoryColors).map(([category, color]) => (
              <filter key={category} id={`glow-${category}`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}
            
            {/* Radial gradient for node backgrounds */}
            <radialGradient id="node-bg-gradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="hsl(var(--card))" stopOpacity="0.9" />
              <stop offset="100%" stopColor="hsl(var(--card))" stopOpacity="0.4" />
            </radialGradient>
          </defs>

          {/* Background circles for visual depth */}
          <g className="bg-circles">
            {[200, 300, 400].map((radius, i) => (
              <circle
                key={`bg-circle-${i}`}
                cx={dimensions.width / 2}
                cy={dimensions.height / 2}
                r={radius}
                fill="none"
                stroke="hsl(var(--border))"
                strokeWidth={1}
                opacity={0.3 - i * 0.1}
                strokeDasharray="8,8"
              />
            ))}
          </g>

          {/* Connections */}
          <g className="connections">
            {connections.map(({ from, to, opacity, strength, types }, i) => {
              const strokeWidth = Math.min(4, 1 + strength * 0.5);
              const isHovered = hoveredConnection === i;
              
              return (
                <g key={`conn-${i}`}>
                  {/* Invisible wider line for easier hover */}
                  <line
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke="transparent"
                    strokeWidth={Math.max(12, strokeWidth * 3)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredConnection(i)}
                    onMouseLeave={() => setHoveredConnection(null)}
                  />
                  {/* Visible connection line */}
                  <line
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke={isHovered ? 'hsl(var(--primary))' : `hsl(var(--muted-foreground))`}
                    strokeWidth={isHovered ? strokeWidth + 1 : strokeWidth}
                    opacity={opacity * animationProgress}
                    style={{
                      strokeDasharray: strength >= 5 ? 'none' : '4,4',
                      animation: strength < 5 ? `dash 20s linear infinite` : undefined,
                      transition: 'all 0.2s ease',
                    }}
                  />
                  {/* Connection midpoint label for strong connections */}
                  {strength >= 5 && (
                    <g>
                      <circle
                        cx={(from.x + to.x) / 2}
                        cy={(from.y + to.y) / 2}
                        r={6}
                        fill="hsl(var(--primary))"
                        opacity={animationProgress}
                      />
                      <text
                        x={(from.x + to.x) / 2}
                        y={(from.y + to.y) / 2 + 3}
                        textAnchor="middle"
                        className="text-[8px] fill-white font-bold"
                        opacity={animationProgress}
                      >
                        {strength}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>

          {/* Node labels on hover */}
          {nodes.map((node) => (
            hoveredNode === node.resource.id && (
              <g key={`label-${node.resource.id}`}>
                <rect
                  x={node.x - 60}
                  y={node.y - 45}
                  width={120}
                  height={24}
                  rx={4}
                  fill="hsl(var(--card))"
                  stroke={node.color}
                  strokeWidth={1}
                  opacity={0.95}
                />
                <text
                  x={node.x}
                  y={node.y - 30}
                  textAnchor="middle"
                  className="text-xs font-medium fill-foreground"
                >
                  {node.resource.name.length > 15 
                    ? node.resource.name.substring(0, 15) + '...' 
                    : node.resource.name}
                </text>
              </g>
            )
          ))}

          {/* Nodes */}
          {nodes.map((node) => {
            const isHovered = hoveredNode === node.resource.id;
            const isSelected = selectedId === node.resource.id;
            const size = isHovered || isSelected ? 20 : 14;
            
            return (
              <g key={node.resource.id}>
                {/* Outer glow ring */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={size + 8}
                  fill="none"
                  stroke={node.color}
                  strokeWidth={2}
                  opacity={isHovered || isSelected ? 0.6 : 0.2 * animationProgress}
                  style={{
                    filter: `blur(4px)`,
                    transition: 'all 0.3s ease',
                  }}
                />
                
                {/* Main node circle */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={size}
                  fill={node.color}
                  opacity={animationProgress}
                  style={{
                    filter: isHovered || isSelected ? `url(#glow-${node.resource.category})` : undefined,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: `scale(${isHovered || isSelected ? 1.2 : 1})`,
                    transformOrigin: `${node.x}px ${node.y}px`,
                  }}
                  onMouseEnter={() => setHoveredNode(node.resource.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={() => onResourceClick(node.resource)}
                />
                
                {/* Inner highlight */}
                <circle
                  cx={node.x - size * 0.3}
                  cy={node.y - size * 0.3}
                  r={size * 0.3}
                  fill="white"
                  opacity={0.3}
                />
                
                {/* Verified badge */}
                {node.resource.verified && (
                  <g transform={`translate(${node.x + size * 0.5}, ${node.y - size * 0.5})`}>
                    <circle
                      cx={0}
                      cy={0}
                      r={6}
                      fill="hsl(var(--primary))"
                      stroke="white"
                      strokeWidth={1}
                    />
                    <path
                      d="M -3 0 L -1 2 L 3 -2"
                      fill="none"
                      stroke="white"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Hovered Node Details Card */}
      {hoveredNode && (
        <div 
          className="absolute top-4 right-4 animate-in fade-in slide-in-from-right-4 duration-300"
          style={{ maxWidth: '320px' }}
        >
          {(() => {
            const node = nodes.find(n => n.resource.id === hoveredNode);
            if (!node) return null;
            
            const resource = node.resource;
            
            return (
              <Card className="shadow-xl border-2" style={{ borderColor: node.color }}>
                <div className="h-1.5 rounded-t" style={{ background: `linear-gradient(90deg, ${node.color}, ${node.color}80)` }} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base truncate">
                        {resource.name}
                      </CardTitle>
                      <Badge
                        variant="secondary"
                        className="text-xs font-normal mt-1"
                        style={{
                          backgroundColor: `${node.color}15`,
                          color: node.color,
                        }}
                      >
                        {categoryLabels[resource.category]}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setHoveredNode(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {resource.description}
                  </p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0" />
                      <span className="truncate">{resource.address}</span>
                    </div>
                    {resource.hours && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4 shrink-0" />
                        <span>{resource.hours}</span>
                      </div>
                    )}
                    {resource.contact?.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4 shrink-0" />
                        <span>{resource.contact.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    {resource.contact?.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `tel:${resource.contact!.phone}`;
                        }}
                      >
                        <Phone className="h-3 w-3 mr-1" />
                        Call
                      </Button>
                    )}
                    {resource.contact?.website && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(resource.contact!.website, '_blank');
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Website
                      </Button>
                    )}
                  </div>
                  
                  {resource.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2 border-t">
                      {resource.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 p-4 rounded-xl bg-card/80 backdrop-blur border">
        <h3 className="text-sm font-semibold mb-3">Categories</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {Object.entries(categoryLabels).map(([category, label]) => (
            <div
              key={category}
              className="flex items-center gap-2 text-xs text-muted-foreground"
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: categoryColors[category as ResourceCategory] }}
              />
              <span>{label}</span>
            </div>
          ))}
        </div>
        
        {/* Connection Legend */}
        <div className="mt-4 pt-4 border-t">
          <h3 className="text-sm font-semibold mb-2">Connections</h3>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-muted-foreground rounded" />
              <span>Strong connection (5+ shared elements)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 bg-muted-foreground rounded" style={{ borderStyle: 'dashed', borderWidth: '1px 0 0 0', borderColor: 'hsl(var(--muted-foreground))' }} />
              <span>Light connection</span>
            </div>
            <div className="flex items-center gap-2 mt-2 text-[10px]">
              <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary">Shared tags</span>
              <span className="px-1.5 py-0.5 rounded bg-accent/10 text-accent">Nearby</span>
              <span className="px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">Complementary</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Connection Tooltip */}
      {hoveredConnection !== null && connections[hoveredConnection] && (
        <div className="absolute bottom-4 left-4 animate-in fade-in slide-in-from-left-4 duration-200">
          <Card className="shadow-lg border-primary/30 bg-primary/5 backdrop-blur">
            <CardContent className="p-3 text-xs">
              <p className="font-semibold mb-1">
                {connections[hoveredConnection].from.resource.name} ↔ {connections[hoveredConnection].to.resource.name}
              </p>
              <div className="flex gap-2 flex-wrap">
                {connections[hoveredConnection].types.map((type, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">
                    {type}
                  </span>
                ))}
              </div>
              <p className="mt-1 text-muted-foreground">
                Strength: {connections[hoveredConnection].strength}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <style>{`
        @keyframes dash {
          to {
            stroke-dashoffset: -100;
          }
        }
      `}</style>
    </div>
  );
}

// Easing function
function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}